<template>
  <component
    :is="tag"
    class="Button"
    :class="[
      `Button--${size}`,
      `Button--${variant}`,
      `Button--${color}`,
      { 'is-loading': loading },
      { 'is-disabled': disabled },
      { 'is-active': active },
    ]"
    :disabled="isDisabled"
    @click="handleClick"
  >
    <Icon
      v-if="leftIcon || loading"
      data-test-id="Button-leftIcon"
      class="Button-icon"
      :icon="loading ? 'loading' : leftIcon!"
      :size
      :flipRtl
      :class="{ 'u-spin': loading }"
    />

    <span v-if="$slots.default" class="Button-label">
      <slot />
    </span>

    <Icon
      v-if="rightIcon"
      data-test-id="Button-rightIcon"
      class="Button-icon"
      :icon="rightIcon"
      :size
      :flipRtl
    />
  </component>
</template>

<script setup lang="ts">
import type { Size, Status } from '@cvo/ui/types/designSystem';
import { computed, type Component } from 'vue';
import Icon from '../Icon/Icon.vue';
import type { IconName } from '../Icon/Icon.model';

export interface ActionButtonProps {
  /** Rendering root element or component (e.g. NuxtLink). Defaults to 'button' */
  tag?: string | Component;
  /** Button size variant */
  size?: Exclude<Size, 'xl'>;
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'invert' | 'list' | 'elevated';
  /** Button color theme */
  color?: 'brand' | Status;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Show left icon */
  leftIcon?: IconName;
  /** Show right icon */
  rightIcon?: IconName;
  /** Loading mode */
  loading?: boolean;
  /** Mirrors both icons in RTL. Use for directional icons such as arrows. */
  flipRtl?: boolean;
  /** Shows a list button as the selected item, e.g. the current page */
  active?: boolean;
}

const props = withDefaults(defineProps<ActionButtonProps>(), {
  tag: 'button',
  size: 'md',
  variant: 'primary',
  color: 'brand',
  disabled: false,
  loading: false,
  flipRtl: false,
  active: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

// Undefined rather than false, to skip the attribute in the DOM entirely.
const isDisabled = computed(() => props.disabled || props.loading || undefined);

/**
 * Handles click events and prevents execution when button is disabled or loading.
 * Checks internal component state rather than DOM attributes for security.
 */
function handleClick(event: MouseEvent) {
  if (isDisabled.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }
  emit('click', event);
}
</script>

<style lang="scss">
@use 'sass:color';
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';

/* Available css custom properties::after
 * --button-color: sets the color for primary background and secondary border
 * --button-radius: override the default border radius
 * --button-active-bg: background of an active list button
 * --button-active-shadow: shadow of an active list button
 */

// Mixins for button variants
@mixin button-radius(
  $brand-radius,
  $danger-radius,
  $success-radius,
  $warning-radius,
  $info-radius,
  $invert-radius,
  $list-radius,
  $elevated-radius
) {
  &.Button--brand { border-radius: var(--button-radius, $brand-radius); }
  &.Button--danger { border-radius: var(--button-radius, $danger-radius); }
  &.Button--success { border-radius: var(--button-radius, $success-radius); }
  &.Button--warning { border-radius: var(--button-radius, $warning-radius); }
  &.Button--information { border-radius: var(--button-radius, $info-radius); }
  &.Button--invert { border-radius: var(--button-radius, $invert-radius); }
  &.Button--list { border-radius: var(--button-radius, $list-radius); }
  // Variant-keyed radii come last so they beat the colour-keyed ones above.
  &.Button--elevated { border-radius: var(--button-radius, $elevated-radius); }
}

@function get-dynamic-color($color, $is-dynamic: true) {
  @if not $is-dynamic {
    @return $color;
  }
  @if color.alpha($color) == 0 {
    @return $color;
  }
  @return var(--button-color, $color);
}

// Icons inherit the text color unless the theme gives a state its own icon token, so
// external text color overrides keep recoloring the icon too.
@function has-own-icon-colors($icon-text-pairs) {
  @each $icon, $text in $icon-text-pairs {
    @if $icon != null and $icon != $text {
      @return true;
    }
  }
  @return false;
}

@mixin button-variant(
  $enabled-text,
  $enabled-bg,
  $enabled-border,
  $border-stroke: 0,
  $hover-text,
  $hover-bg,
  $hover-border,
  $disabled-text,
  $disabled-bg,
  $disabled-border,
  $loading-text,
  $loading-bg,
  $loading-border,
  $dynamic-text: false, // Indicates if text color should use --button-color variable
  $dynamic-bg: false, // Indicates if background color should use --button-color variable
  $dynamic-border: false, // Indicates if border color should use --button-color variable
  $enabled-icon: null,
  $hover-icon: null,
  $disabled-icon: null,
  $loading-icon: null
) {
  $own-icons: has-own-icon-colors((
    $enabled-icon $enabled-text,
    $hover-icon $hover-text,
    $disabled-icon $disabled-text,
    $loading-icon $loading-text,
  ));

  color: get-dynamic-color($enabled-text, $dynamic-text);
  background-color: get-dynamic-color($enabled-bg, $dynamic-bg);
  border: $border-stroke solid get-dynamic-color($enabled-border, $dynamic-border);
  transition: background vars.$transition-fast ease-in-out;

  @if $own-icons {
    .Button-icon {
      color: get-dynamic-color($enabled-icon, $dynamic-text);
    }
  }

  // Touch devices keep :hover after a tap, which would leave the button painted as
  // hovered, so only devices with a real pointer get the hover state.
  @media (hover: hover) {
    &:hover:not(.is-disabled):not(.is-loading) {
      background: get-dynamic-color($hover-bg, $dynamic-bg);
      border-color: get-dynamic-color($hover-border, $dynamic-border);
      color: $hover-text;

      @if $own-icons {
        .Button-icon {
          color: $hover-icon;
        }
      }
    }
  }

  &.is-disabled {
    background-color: $disabled-bg;
    border-color: $disabled-border;
    color: $disabled-text;

    @if $own-icons {
      .Button-icon {
        color: $disabled-icon;
      }
    }
  }

  &.is-loading {
    background-color: $loading-bg;
    border-color: $loading-border;
    color: $loading-text;

    @if $own-icons {
      .Button-icon {
        color: $loading-icon;
      }
    }
  }
}

@layer cvo-ui {
  .Button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    color: var(--button-color, vars.$atoms-actionbutton-primary-enabled-bg-color);
    font-family: vars.$font-family-body;
    font-weight: vars.$typography-weight-strong;

    border: none;
    border-radius: var(--button-radius, vars.$atoms-actionbutton-primary-md-border-radius);
    cursor: pointer;

    outline: none;
    white-space: nowrap;
    text-decoration: none;

    &.is-disabled {
      cursor: not-allowed;
    }

    &.is-loading {
      cursor: wait;
    }

    &:focus-visible {
      outline: 2px solid var(--button-color, vars.$color-brand-primary-900);
      outline-offset: 2px;
    }

    // Size variants
    &--xs {
      padding: vars.$dimensions-d-1;
      font-size: vars.$font-size-xs;
      letter-spacing: -0.36px;
      gap: vars.$dimensions-d-1;

      @include button-radius(
        $brand-radius: vars.$atoms-actionbutton-primary-xs-border-radius,
        $danger-radius: vars.$atoms-actionbutton-danger-primary-xs-border-radius,
        $success-radius: vars.$atoms-actionbutton-success-primary-xs-border-radius,
        $warning-radius: vars.$atoms-actionbutton-warning-primary-xs-border-radius,
        $info-radius: vars.$atoms-actionbutton-information-primary-xs-border-radius,
        $invert-radius: vars.$atoms-actionbutton-invert-xs-border-radius,
        $list-radius: vars.$atoms-actionbutton-list-sm-border-radius,
        $elevated-radius: vars.$atoms-actionbutton-elevated-md-border-radius
      );

      min-width: 48px;
      min-height: 20px;
      line-height: 12px;
    }

    &--sm {
      padding: vars.$dimensions-d-2;
      font-size: vars.$font-size-sm;
      letter-spacing: -0.42px;
      gap: vars.$dimensions-d-1;

      @include button-radius(
        $brand-radius: vars.$atoms-actionbutton-primary-sm-border-radius,
        $danger-radius: vars.$atoms-actionbutton-danger-primary-sm-border-radius,
        $success-radius: vars.$atoms-actionbutton-success-primary-sm-border-radius,
        $warning-radius: vars.$atoms-actionbutton-warning-primary-sm-border-radius,
        $info-radius: vars.$atoms-actionbutton-information-primary-sm-border-radius,
        $invert-radius: vars.$atoms-actionbutton-invert-sm-border-radius,
        $list-radius: vars.$atoms-actionbutton-list-sm-border-radius,
        $elevated-radius: vars.$atoms-actionbutton-elevated-md-border-radius
      );

      min-width: 80px;
      min-height: 32px;
      line-height: 16px;
    }

    &--md {
      padding: vars.$dimensions-d-3;
      font-size: vars.$font-size-md;
      letter-spacing: -0.48px;
      gap: vars.$dimensions-d-2;

      @include button-radius(
        $brand-radius: vars.$atoms-actionbutton-primary-md-border-radius,
        $danger-radius: vars.$atoms-actionbutton-danger-primary-md-border-radius,
        $success-radius: vars.$atoms-actionbutton-success-primary-md-border-radius,
        $warning-radius: vars.$atoms-actionbutton-warning-primary-md-border-radius,
        $info-radius: vars.$atoms-actionbutton-information-primary-md-border-radius,
        $invert-radius: vars.$atoms-actionbutton-invert-md-border-radius,
        $list-radius: vars.$atoms-actionbutton-list-md-border-radius,
        $elevated-radius: vars.$atoms-actionbutton-elevated-md-border-radius
      );

      min-width: 140px;
      min-height: 44px;
      line-height: 20px;
    }

    &--lg {
      padding: vars.$dimensions-d-4;
      font-size: vars.$font-size-lg;
      letter-spacing: -0.6px;
      gap: vars.$dimensions-d-3;

      @include button-radius(
        $brand-radius: vars.$atoms-actionbutton-primary-lg-border-radius,
        $danger-radius: vars.$atoms-actionbutton-danger-primary-lg-border-radius,
        $success-radius: vars.$atoms-actionbutton-success-primary-lg-border-radius,
        $warning-radius: vars.$atoms-actionbutton-warning-primary-lg-border-radius,
        $info-radius: vars.$atoms-actionbutton-information-primary-lg-border-radius,
        $invert-radius: vars.$atoms-actionbutton-invert-lg-border-radius,
        $list-radius: vars.$atoms-actionbutton-list-lg-border-radius,
        $elevated-radius: vars.$atoms-actionbutton-elevated-md-border-radius
      );

      min-width: 156px;
      min-height: 56px;
      line-height: 24px;
    }

    // Primary variant
    &--primary {
      &.Button--brand {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-primary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-primary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-primary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-primary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-primary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-primary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-primary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-primary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-primary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-primary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-primary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-primary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-primary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-primary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-primary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-primary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-primary-loading-border-color,
          $dynamic-bg: true,
          $dynamic-border: true
        );
      }

      &.Button--danger {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-danger-primary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-danger-primary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-danger-primary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-danger-primary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-danger-primary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-danger-primary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-danger-primary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-danger-primary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-danger-primary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-danger-primary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-danger-primary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-danger-primary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-danger-primary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-danger-primary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-danger-primary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-danger-primary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-danger-primary-loading-border-color,
          $dynamic-bg: true,
          $dynamic-border: true
        );
      }
      &.Button--success {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-success-primary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-success-primary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-success-primary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-success-primary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-success-primary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-success-primary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-success-primary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-success-primary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-success-primary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-success-primary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-success-primary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-success-primary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-success-primary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-success-primary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-success-primary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-success-primary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-success-primary-loading-border-color,
          $dynamic-bg: true,
          $dynamic-border: true
        );
      }
      &.Button--warning {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-warning-primary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-warning-primary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-warning-primary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-warning-primary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-warning-primary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-warning-primary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-warning-primary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-warning-primary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-warning-primary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-warning-primary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-warning-primary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-warning-primary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-warning-primary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-warning-primary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-warning-primary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-warning-primary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-warning-primary-loading-border-color,
          $dynamic-bg: true,
          $dynamic-border: true
        );
      }
      &.Button--information {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-information-primary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-information-primary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-information-primary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-information-primary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-information-primary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-information-primary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-information-primary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-information-primary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-information-primary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-information-primary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-information-primary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-information-primary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-information-primary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-information-primary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-information-primary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-information-primary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-information-primary-loading-border-color,
          $dynamic-bg: true,
          $dynamic-border: true
        );
      }
    }

    // Secondary variant
    &--secondary {
      &.Button--brand {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-secondary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-secondary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-secondary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-secondary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-secondary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-secondary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-secondary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-secondary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-secondary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-secondary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-secondary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-secondary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-secondary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-secondary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-secondary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-secondary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-secondary-loading-border-color,
          $dynamic-text: true,
          $dynamic-border: true
        );
      }
      &.Button--danger {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-danger-secondary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-danger-secondary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-danger-secondary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-danger-secondary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-danger-secondary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-danger-secondary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-danger-secondary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-danger-secondary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-danger-secondary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-danger-secondary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-danger-secondary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-danger-secondary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-danger-secondary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-danger-secondary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-danger-secondary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-danger-secondary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-danger-secondary-loading-border-color,
          $dynamic-text: true,
          $dynamic-border: true
        );
      }
      &.Button--success {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-success-secondary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-success-secondary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-success-secondary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-success-secondary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-success-secondary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-success-secondary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-success-secondary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-success-secondary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-success-secondary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-success-secondary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-success-secondary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-success-secondary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-success-secondary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-success-secondary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-success-secondary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-success-secondary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-success-secondary-loading-border-color,
          $dynamic-text: true,
          $dynamic-border: true
        );
      }
      &.Button--warning {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-warning-secondary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-warning-secondary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-warning-secondary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-warning-secondary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-warning-secondary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-warning-secondary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-warning-secondary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-warning-secondary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-warning-secondary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-warning-secondary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-warning-secondary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-warning-secondary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-warning-secondary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-warning-secondary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-warning-secondary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-warning-secondary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-warning-secondary-loading-border-color,
          $dynamic-text: true,
          $dynamic-border: true
        );
      }
      &.Button--information {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-information-secondary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-information-secondary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-information-secondary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-information-secondary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-information-secondary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-information-secondary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-information-secondary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-information-secondary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-information-secondary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-information-secondary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-information-secondary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-information-secondary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-information-secondary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-information-secondary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-information-secondary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-information-secondary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-information-secondary-loading-border-color,
          $dynamic-text: true,
          $dynamic-border: true
        );
      }
    }

    // Tertiary variant
    &--tertiary,
    &--invert {
      min-width: 0;

      .Button-label {
        text-decoration: none;
      }

      // Gated like the colour hover above: touch devices keep :hover after a tap, which
      // would leave the label permanently underlined.
      @media (hover: hover) {
        &:hover:not(.is-disabled):not(.is-loading) .Button-label {
          text-decoration: underline;
        }
      }

      &:focus-visible {
        outline-color: var(--button-color, vars.$atoms-actionbutton-primary-enabled-border-color);
        outline-offset: 2px;
      }

      // Disabled state
      &.is-disabled .Button-label {
        text-decoration: none;
      }
    }

    &--tertiary {
      &.Button--brand {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-tertiary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-tertiary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-tertiary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-tertiary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-tertiary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-tertiary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-tertiary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-tertiary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-tertiary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-tertiary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-tertiary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-tertiary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-tertiary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-tertiary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-tertiary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-tertiary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-tertiary-loading-border-color,
          $dynamic-text: true
        );
      }
      &.Button--danger {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-danger-tertiary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-danger-tertiary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-danger-tertiary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-danger-tertiary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-danger-tertiary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-danger-tertiary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-danger-tertiary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-danger-tertiary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-danger-tertiary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-danger-tertiary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-danger-tertiary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-danger-tertiary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-danger-tertiary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-danger-tertiary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-danger-tertiary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-danger-tertiary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-danger-tertiary-loading-border-color,
          $dynamic-text: true
        );
      }
      &.Button--success {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-success-tertiary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-success-tertiary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-success-tertiary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-success-tertiary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-success-tertiary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-success-tertiary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-success-tertiary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-success-tertiary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-success-tertiary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-success-tertiary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-success-tertiary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-success-tertiary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-success-tertiary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-success-tertiary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-success-tertiary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-success-tertiary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-success-tertiary-loading-border-color,
          $dynamic-text: true
        );
      }
      &.Button--warning {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-warning-tertiary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-warning-tertiary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-warning-tertiary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-warning-tertiary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-warning-tertiary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-warning-tertiary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-warning-tertiary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-warning-tertiary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-warning-tertiary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-warning-tertiary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-warning-tertiary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-warning-tertiary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-warning-tertiary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-warning-tertiary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-warning-tertiary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-warning-tertiary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-warning-tertiary-loading-border-color,
          $dynamic-text: true
        );
      }
      &.Button--info {
        @include button-variant(
          $enabled-text: vars.$atoms-actionbutton-information-tertiary-enabled-text-color,
          $enabled-icon: vars.$atoms-actionbutton-information-tertiary-enabled-icon-color,
          $enabled-bg: vars.$atoms-actionbutton-information-tertiary-enabled-bg-color,
          $enabled-border: vars.$atoms-actionbutton-information-tertiary-enabled-border-color,
          $border-stroke: vars.$atoms-actionbutton-information-tertiary-border-stroke,
          $hover-text: vars.$atoms-actionbutton-information-tertiary-hover-text-color,
          $hover-icon: vars.$atoms-actionbutton-information-tertiary-hover-icon-color,
          $hover-bg: vars.$atoms-actionbutton-information-tertiary-hover-bg-color,
          $hover-border: vars.$atoms-actionbutton-information-tertiary-hover-border-color,
          $disabled-text: vars.$atoms-actionbutton-information-tertiary-disabled-text-color,
          $disabled-icon: vars.$atoms-actionbutton-information-tertiary-disabled-icon-color,
          $disabled-bg: vars.$atoms-actionbutton-information-tertiary-disabled-bg-color,
          $disabled-border: vars.$atoms-actionbutton-information-tertiary-disabled-border-color,
          $loading-text: vars.$atoms-actionbutton-information-tertiary-loading-text-color,
          $loading-icon: vars.$atoms-actionbutton-information-tertiary-loading-icon-color,
          $loading-bg: vars.$atoms-actionbutton-information-tertiary-loading-bg-color,
          $loading-border: vars.$atoms-actionbutton-information-tertiary-loading-border-color,
          $dynamic-text: true
        );
      }
    }

    // Invert variant
    &--invert {
      @include button-variant(
        $enabled-text: vars.$atoms-actionbutton-invert-enabled-text-color,
        $enabled-icon: vars.$atoms-actionbutton-invert-enabled-icon-color,
        $enabled-bg: vars.$atoms-actionbutton-invert-enabled-bg-color,
        $enabled-border: vars.$atoms-actionbutton-invert-enabled-border-color,
        $border-stroke: vars.$atoms-actionbutton-invert-border-stroke,
        $hover-text: vars.$atoms-actionbutton-invert-hover-text-color,
        $hover-icon: vars.$atoms-actionbutton-invert-hover-icon-color,
        $hover-bg: vars.$atoms-actionbutton-invert-hover-bg-color,
        $hover-border: vars.$atoms-actionbutton-invert-hover-border-color,
        $disabled-text: vars.$atoms-actionbutton-invert-disabled-text-color,
        $disabled-icon: vars.$atoms-actionbutton-invert-disabled-icon-color,
        $disabled-bg: vars.$atoms-actionbutton-invert-disabled-bg-color,
        $disabled-border: vars.$atoms-actionbutton-invert-disabled-border-color,
        $loading-text: vars.$atoms-actionbutton-invert-loading-text-color,
        $loading-icon: vars.$atoms-actionbutton-invert-loading-icon-color,
        $loading-bg: vars.$atoms-actionbutton-invert-loading-bg-color,
        $loading-border: vars.$atoms-actionbutton-invert-loading-border-color,
        $dynamic-text: true,
      );
    }

    // List variant
    &--list {
      padding-block: vars.$dimensions-d-3;
      padding-inline: vars.$dimensions-d-4;
      gap: vars.$dimensions-d-3;
      justify-content: flex-start;
      font-weight: vars.$typography-weight-strong;
      align-items: flex-start;
      text-align: start;

      &.Button--sm {
        padding-inline: vars.$dimensions-d-2;
      }

      @include button-variant(
        $enabled-text: vars.$atoms-actionbutton-list-enabled-text-color,
        $enabled-icon: vars.$atoms-actionbutton-list-enabled-icon-color,
        $enabled-bg: vars.$atoms-actionbutton-list-enabled-bg-color,
        $enabled-border: vars.$atoms-actionbutton-list-enabled-border-color,
        $hover-text: vars.$atoms-actionbutton-list-hover-text-color,
        $hover-icon: vars.$atoms-actionbutton-list-hover-icon-color,
        $hover-bg: vars.$atoms-actionbutton-list-hover-bg-color,
        $hover-border: vars.$atoms-actionbutton-list-hover-border-color,
        $disabled-text: vars.$atoms-actionbutton-list-disabled-text-color,
        $disabled-icon: vars.$atoms-actionbutton-list-disabled-icon-color,
        $disabled-bg: vars.$atoms-actionbutton-list-disabled-bg-color,
        $disabled-border: vars.$atoms-actionbutton-list-disabled-border-color,
        $loading-text: vars.$atoms-actionbutton-list-enabled-text-color,
        $loading-icon: vars.$atoms-actionbutton-list-enabled-icon-color,
        $loading-bg: vars.$atoms-actionbutton-list-enabled-bg-color,
        $loading-border: vars.$atoms-actionbutton-list-enabled-border-color,
        $dynamic-text: true,
      );

      &:active:not(.is-disabled):not(.is-loading),
      &.is-active,
      &.is-active:hover:not(.is-disabled):not(.is-loading) {
        background-color: var(--button-active-bg, #{get-dynamic-color(vars.$atoms-actionbutton-list-active-bg-color)});
        border-color: get-dynamic-color(vars.$atoms-actionbutton-list-active-border-color);
        color: vars.$atoms-actionbutton-list-active-text-color;
      }

      &.is-active {
        box-shadow: var(--button-active-shadow, #{vars.$shadow-md});
      }

      &:active:not(.is-disabled):not(.is-loading) .Button-icon,
      &.is-active .Button-icon,
      &.is-active:hover:not(.is-disabled):not(.is-loading) .Button-icon {
        color: vars.$atoms-actionbutton-list-active-icon-color;
      }
    }

    // Elevated variant
    &--elevated {
      min-width: 0;
      box-shadow: vars.$shadow-md;

      @include button-variant(
        $enabled-text: vars.$atoms-actionbutton-elevated-enabled-text-color,
        $enabled-icon: vars.$atoms-actionbutton-elevated-enabled-icon-color,
        $enabled-bg: vars.$atoms-actionbutton-elevated-enabled-bg-color,
        $enabled-border: vars.$atoms-actionbutton-elevated-enabled-border-color,
        $border-stroke: vars.$atoms-actionbutton-elevated-border-stroke,
        $hover-text: vars.$atoms-actionbutton-elevated-enabled-text-color,
        $hover-icon: vars.$atoms-actionbutton-elevated-enabled-icon-color,
        $hover-bg: vars.$atoms-actionbutton-elevated-enabled-bg-color,
        $hover-border: vars.$atoms-actionbutton-elevated-enabled-border-color,
        $disabled-text: vars.$atoms-actionbutton-elevated-enabled-text-color,
        $disabled-icon: vars.$atoms-actionbutton-elevated-enabled-icon-color,
        $disabled-bg: vars.$atoms-actionbutton-elevated-enabled-bg-color,
        $disabled-border: vars.$atoms-actionbutton-elevated-enabled-border-color,
        $loading-text: vars.$atoms-actionbutton-elevated-enabled-text-color,
        $loading-icon: vars.$atoms-actionbutton-elevated-enabled-icon-color,
        $loading-bg: vars.$atoms-actionbutton-elevated-enabled-bg-color,
        $loading-border: vars.$atoms-actionbutton-elevated-enabled-border-color,
      );
    }

    &-label {
      display: flex;
      align-items: center;
    }
  }
}
</style>